import React, { useContext, useCallback, useState } from "react";
import Table, {
  TableCell,
  TableRow,
  TableSectionHeader,
} from "@amzn/meridian/table";
import { FormattedMessage, injectIntl } from "react-intl";
import { PVAOverviewContext } from "../overview/context/PVAOverviewContext";
import { CycleFilterContext } from "../overview/context/CycleFilterContext";
import {
  getDataPointRange,
  getActualInductMHEDatapoints,
  getActualInductManualDatapoints,
  getPlannedInductTotalDatapoints,
  getPlannedInductMHEDatapoints,
  getPlannedInductManualDatapoints,
  getLastDataPointRange,
  capDecimal,
  getFilteredPPAData,
  getPlannedHeadCountMap,
  getPlannedMap,
  getProcessPathPlannedFPHMap,
  isValidForPlannedFph,
  getPlannedFPHOfProcess,
  getTargetPphProcess,
  convertToFixedDP,
} from "../overview/utils/utils";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import chevronDownSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import Icon from "@amzn/meridian/icon";
import {
  ONE_HOUR,
  PICK_STAGE,
  SITE_SUPPORT,
  SORT,
  UTR,
  headerWidth,
  DEFAULT_HEAD_COUNT,
  OTR,
  SWA,
  RTS,
  NonCoreSupport,
  ACTUALS,
  SLASH,
  INBOUND_ACTUALS,
  INBOUND_PLANNED,
  INDUCT_ACTUALS,
  INDUCT_PLANNED,
  STOW_ACTUALS,
  STOW_PLANNED,
  INDUCT,
  AUTO_SCAN_INDUCT_LOADER,
  STOW,
  PLANNED_HC_PROCESS_ALIAS,
} from "../overview/utils/PPAConstants";
import infoKnockoutTokens from "@amzn/meridian-tokens/base/icon/info-knockout";
import Tooltip from "@amzn/meridian/tooltip";
import { DEFAULT_CYCLE } from "../overview/utils/Constants";

const PackageOverviewInbound = ({ intl }) => {
  const { pvaData } = useContext(PVAOverviewContext);
  const { cycles } = useContext(CycleFilterContext);
  const selectedCycle = cycles.selectedCycles
    ? cycles.selectedCycles[0]
    : DEFAULT_CYCLE;
  // fetch shiftPlans from pvaData
  let shiftPlans = pvaData.shiftPlans.filter(
    (shiftPlan) => shiftPlan.cycleName === selectedCycle,
  );
  let hasPlan = shiftPlans.length === 0 ? null : true;
  const [all, setAll] = useState(false);
  const filteredPPAData =
    pvaData.pvaTableData.granularity === ONE_HOUR
      ? getFilteredPPAData(pvaData)
      : {};
  const lengthOfDataPointList =
    pvaData.overviewData.flowPVAData[pvaData.pvaTableData.granularity][0]
      .dataPointList.length;
  const plannedHoursMap = getPlannedHeadCountMap(pvaData);
  let processes = [INDUCT, STOW, AUTO_SCAN_INDUCT_LOADER];
  const plannedProcessPathMap = getProcessPathPlannedFPHMap(
    shiftPlans,
    processes,
  );
  let targetPphInduct = getTargetPphProcess(pvaData.pphData, "Induct");
  let targetPphStow = getTargetPphProcess(pvaData.pphData, "Stow");
  const onClickAll = useCallback(() => {
    setAll(!all);
    setReceive(!all);
    setStow(!all);
    setInduct(!all);
    setInductPlanned(!all);
    setInductActual(!all);
    setTph(!all);
    setLabour(!all);
    setSortPA(!all);
    setStagePA(!all);
    setUtrPA(!all);
    setSiteSupportPA(!all);
    setOtrPA(!all);
    setSwaPA(!all);
    setRtsPA(!all);
    setNonCoreSupportPA(!all);
  }, [all]);
  const [receive, setReceive] = useState(false);
  const onClickReceive = useCallback(() => setReceive(!receive), [receive]);
  const [stow, setStow] = useState(false);
  const onClickStow = useCallback(() => setStow(!stow), [stow]);
  const [induct, setInduct] = useState(false);
  const onClickInduct = useCallback(() => setInduct(!induct), [induct]);
  const [inductPlanned, setInductPlanned] = useState(false);
  const onClickInductPlanned = useCallback(
    () => setInductPlanned(!inductPlanned),
    [inductPlanned],
  );
  const [inductActual, setInductActual] = useState(false);
  const onClickInductActual = useCallback(
    () => setInductActual(!inductActual),
    [inductActual],
  );
  const [tph, setTph] = useState(false);
  const onClickTph = useCallback(() => setTph(!tph), [tph]);
  const [labour, setLabour] = useState(false);
  const onClickLabour = useCallback(() => setLabour(!labour), [labour]);
  const [sortPA, setSortPA] = useState(false);
  const onClickSortPA = useCallback(() => setSortPA(!sortPA), [sortPA]);
  const [stagePA, setStagePA] = useState(false);
  const onClickStagePA = useCallback(() => setStagePA(!stagePA), [stagePA]);
  const [utrPA, setUtrPA] = useState(false);
  const onClickUtrPA = useCallback(() => setUtrPA(!utrPA), [utrPA]);
  const [siteSupportPA, setSiteSupportPA] = useState(false);
  const onClickSiteSupportPA = useCallback(
    () => setSiteSupportPA(!siteSupportPA),
    [siteSupportPA],
  );
  const [otrPA, setOtrPA] = useState(false);
  const onClickOtrPA = useCallback(() => setOtrPA(!otrPA), [otrPA]);
  const [swaPA, setSwaPA] = useState(false);
  const onClickSwaPA = useCallback(() => setSwaPA(!swaPA), [swaPA]);
  const [rtsPA, setRtsPA] = useState(false);
  const onClickRtsPA = useCallback(() => setRtsPA(!rtsPA), [rtsPA]);
  const [nonCoreSupportPA, setNonCoreSupportPA] = useState(false);
  const onClickNonCoreSupportPA = useCallback(
    () => setNonCoreSupportPA(!nonCoreSupportPA),
    [nonCoreSupportPA],
  );

  const displayStation = () => {
    return (
      pvaData.pvaTableData.granularity === ONE_HOUR &&
      Object.keys(filteredPPAData).length > 0
    );
  };

  const generateMainProcessTotals = (filteredPPAData, mainProcess) => {
    // Generating Main Process on PVA Table
    return filteredPPAData.map((data) => {
      const plannedHeadCount = getPlannedHeadCount(data.time, mainProcess);
      return (
        // if hasPlan is there then P/A, else only A.
        data.headCountDataMap[mainProcess] &&
          data.headCountDataMap[mainProcess][ACTUALS] ? (
          hasPlan ? (
            <TableCell key={data.time}>
              {plannedHeadCount +
                SLASH +
                data.headCountDataMap[mainProcess][ACTUALS]}
            </TableCell>
          ) : (
            <TableCell key={data.time}>
              {data.headCountDataMap[mainProcess][ACTUALS]}
            </TableCell>
          )
        ) : hasPlan ? (
          <TableCell key={data.time}>
            {plannedHeadCount + SLASH + DEFAULT_HEAD_COUNT}
          </TableCell>
        ) : (
          <TableCell key={data.time}> {DEFAULT_HEAD_COUNT} </TableCell>
        )
      );
    });
  };

  const generateSubProcessTotals = (
    filteredPPAData,
    processMap,
    mainProcess,
  ) => {
    // Generating Sub Processes on PVA Table for a Main Process using processMap from Constants
    return Object.keys(processMap).map((key) => (
      <TableRow>
        <TableCell width={headerWidth}>
          &nbsp;&nbsp;&nbsp;&nbsp;
          <FormattedMessage
            id={processMap[key].id}
            defaultMessage={processMap[key].name}
          ></FormattedMessage>
        </TableCell>
        {filteredPPAData.map((data) => {
          const plannedHeadCount = getPlannedHeadCount(
            data.time,
            processMap[key].alias,
          );
          return Object.keys(data.headCountDataMap).length > 1 &&
            data.headCountDataMap[mainProcess][processMap[key].alias] ? (
            hasPlan ? (
              <TableCell key={data.time}>
                {plannedHeadCount +
                  SLASH +
                  capDecimal(
                    data.headCountDataMap[mainProcess][processMap[key].alias][0]
                      .headCount,
                  )}{" "}
              </TableCell>
            ) : (
              <TableCell key={data.time}>
                {capDecimal(
                  data.headCountDataMap[mainProcess][processMap[key].alias][0]
                    .headCount,
                )}{" "}
              </TableCell>
            )
          ) : hasPlan ? (
            <TableCell key={data.time}>
              {plannedHeadCount + SLASH + DEFAULT_HEAD_COUNT}{" "}
            </TableCell>
          ) : (
            <TableCell key={data.time}> {DEFAULT_HEAD_COUNT} </TableCell>
          );
        })}
      </TableRow>
    ));
  };

  const SelfContainedExpandableRow = ({ children, rowComponents }) => {
    const [open, setOpen] = useState(false);
    return (
      <TableRow
        open={open}
        onClick={() => setOpen(!open)}
        rowComponents={rowComponents}
      >
        {children}
      </TableRow>
    );
  };

  function getPlannedHeadCount(time, alias) {
    let updatedDateTime = `${time}:00`;
    alias = PLANNED_HC_PROCESS_ALIAS[alias] || alias;
    let headcountMap =
      plannedHoursMap.get(updatedDateTime) ||
      getPlannedMap(plannedHoursMap, updatedDateTime);
    let plannedHeadCount = headcountMap ? headcountMap.get(alias) || 0 : 0;
    return convertToFixedDP(plannedHeadCount);
  }

  return (
    <Table
      headerRows={1}
      showDividers={false}
      spacing={"small"}
      rowComponents={[TableRow, SelfContainedExpandableRow]}
      showExpandedDividers={false}
      nestedRows={true}
      headerColumns={1}
      stickyHeaderColumn={true}
      stickyHeaderRow={true}
      fixHeaderRows={true}
    >
      <TableRow open={all} onClick={onClickAll}>
        <TableCell>
          {all ? (
            <Icon tokens={chevronDownSmallTokens} className="chevron-heading" />
          ) : (
            <Icon
              tokens={chevronRightSmallTokens}
              className="chevron-heading"
            />
          )}
          <span id="all-color">
            <FormattedMessage id="package_overview.all" defaultMessage="All" />
          </span>
        </TableCell>
        {pvaData.overviewData.flowPVAData[
          pvaData.pvaTableData.granularity
        ][0].dataPointList.map((datapoint, index) => (
          <TableCell key={index}>
            {pvaData.overviewData.flowPVAData[
              pvaData.pvaTableData.granularity
            ][0].dataPointList.length ===
            index + 1
              ? getLastDataPointRange(
                  datapoint,
                  pvaData.overviewData.flowPVAData["15"][0].dataPointList[
                    pvaData.overviewData.flowPVAData["15"][0].dataPointList
                      .length - 1
                  ],
                )
              : getDataPointRange(
                  index,
                  pvaData.overviewData.flowPVAData[
                    pvaData.pvaTableData.granularity
                  ][0].dataPointList,
                )}
          </TableCell>
        ))}
      </TableRow>
      <TableRow />
      <TableRow />
      <TableRow />
      <TableSectionHeader>
        <b>
          <FormattedMessage
            id="package_overview.inbound"
            defaultMessage="Inbound"
          />
        </b>
      </TableSectionHeader>
      {/* Receive */}
      {receive ? (
        <TableRow open={receive} onClick={onClickReceive}>
          <TableCell width={headerWidth}>
            <b>
              <FormattedMessage
                id="package_overview.receive"
                defaultMessage="Receive"
              />
            </b>
            <Tooltip
              position="right"
              title={intl.formatMessage({
                id: "tooltip.package_overview.receive",
              })}
            >
              <Icon className={"custom-tool-tip"} tokens={infoKnockoutTokens} />
            </Tooltip>
          </TableCell>
          <TableRow>
            <TableCell>
              &nbsp;&nbsp;
              <FormattedMessage
                id="package_overview.planned_volume"
                defaultMessage="Planned Volume"
              />
            </TableCell>
            {pvaData.inbound.flowPVAData
              ? pvaData.inbound.flowPVAData[pvaData.pvaTableData.granularity][
                  INBOUND_PLANNED
                ].dataPointList.map((datapoint, index) => (
                  <TableCell key={index}>
                    {datapoint.metricValue < 0
                      ? datapoint.metricValue === -1
                        ? "-"
                        : "0"
                      : datapoint.metricValue}
                  </TableCell>
                ))
              : null}
          </TableRow>
          <TableRow>
            <TableCell>
              &nbsp;&nbsp;
              <FormattedMessage
                id="package_overview.actual_volume"
                defaultMessage="Actual Volume"
              />
            </TableCell>
            {pvaData.inbound.flowPVAData
              ? pvaData.inbound.flowPVAData[pvaData.pvaTableData.granularity][
                  INBOUND_ACTUALS
                ].dataPointList.map((datapoint, index) => (
                  <TableCell key={index}>
                    {datapoint.metricValue < 0
                      ? datapoint.metricValue === -1
                        ? "-"
                        : "0"
                      : datapoint.metricValue}
                  </TableCell>
                ))
              : null}
          </TableRow>
        </TableRow>
      ) : (
        <TableRow open={receive} onClick={onClickReceive}>
          {/* Receive Actuals */}
          <TableCell>
            <b>
              <Icon
                tokens={chevronRightSmallTokens}
                className="chevron-margin"
              />
              <FormattedMessage
                id="package_overview.receive_actuals"
                defaultMessage="Receive Actuals"
              />
            </b>
          </TableCell>

          {pvaData.inbound.flowPVAData
            ? pvaData.inbound.flowPVAData[pvaData.pvaTableData.granularity][
                INBOUND_ACTUALS
              ].dataPointList.map((datapoint, index) => (
                <TableCell key={index}>
                  {datapoint.metricValue < 0
                    ? datapoint.metricValue === -1
                      ? "-"
                      : "0"
                    : datapoint.metricValue}
                </TableCell>
              ))
            : null}
        </TableRow>
      )}
      <TableRow></TableRow>
      {/* Induct */}
      {induct ? (
        <TableRow open={induct} onClick={onClickInduct}>
          <TableCell width={headerWidth}>
            <b>
              <FormattedMessage
                id="package_overview.induct"
                defaultMessage="Induct"
              />
            </b>
            <Tooltip
              position="right"
              title={intl.formatMessage({
                id: "tooltip.package_overview.induct",
              })}
            >
              <Icon className={"custom-tool-tip"} tokens={infoKnockoutTokens} />
            </Tooltip>
          </TableCell>
          {inductPlanned ? (
            [
              <TableRow open={inductPlanned} onClick={onClickInductPlanned}>
                <TableCell width={headerWidth}>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.planned_volume"
                      defaultMessage="Planned Volume"
                    />
                  </b>
                </TableCell>
                {isValidForPlannedFph(hasPlan, pvaData.pvaTableData.granularity)
                  ? getPlannedInductTotalDatapoints(
                      shiftPlans,
                      plannedProcessPathMap,
                      lengthOfDataPointList,
                    ).map((volume, index) => (
                      <TableCell key={index}>{volume}</TableCell>
                    ))
                  : pvaData.overviewData.flowPVAData[
                      pvaData.pvaTableData.granularity
                    ][INDUCT_PLANNED].dataPointList.map((datapoint, index) => (
                      <TableCell key={index}>
                        {datapoint.metricValue < 0
                          ? datapoint.metricValue === -1
                            ? "-"
                            : "0"
                          : datapoint.metricValue}
                      </TableCell>
                    ))}
              </TableRow>,
              <TableRow highlight={inductPlanned}>
                <TableCell>
                  &nbsp;&nbsp;
                  <FormattedMessage
                    id="package_overview.automation_volume"
                    defaultMessage="Automation Volume"
                  />
                </TableCell>
                {isValidForPlannedFph(hasPlan, pvaData.pvaTableData.granularity)
                  ? getPlannedInductMHEDatapoints(
                      shiftPlans,
                      plannedProcessPathMap,
                      lengthOfDataPointList,
                    ).map((volume, index) => (
                      <TableCell key={index}>{volume}</TableCell>
                    ))
                  : pvaData.overviewData.flowPVAData[
                      pvaData.pvaTableData.granularity
                    ][INDUCT_PLANNED].dataPointList.map((datapoint, index) => (
                      <TableCell key={index}>-</TableCell>
                    ))}
              </TableRow>,
              <TableRow highlight={inductPlanned}>
                <TableCell>
                  &nbsp;&nbsp;
                  <FormattedMessage
                    id="package_overview.manual_volume"
                    defaultMessage="Manual Volume"
                  />
                </TableCell>
                {isValidForPlannedFph(hasPlan, pvaData.pvaTableData.granularity)
                  ? getPlannedInductManualDatapoints(
                      shiftPlans,
                      plannedProcessPathMap,
                      lengthOfDataPointList,
                    ).map((volume, index) => (
                      <TableCell key={index}>{volume}</TableCell>
                    ))
                  : pvaData.overviewData.flowPVAData[
                      pvaData.pvaTableData.granularity
                    ][INDUCT_PLANNED].dataPointList.map((datapoint, index) => (
                      <TableCell key={index}>-</TableCell>
                    ))}
              </TableRow>,
            ]
          ) : (
            <TableRow open={inductPlanned} onClick={onClickInductPlanned}>
              <TableCell width={headerWidth}>
                <b>
                  <Icon
                    tokens={chevronRightSmallTokens}
                    className="chevron-margin"
                  />
                  <FormattedMessage
                    id="package_overview.planned_volume"
                    defaultMessage="Planned Volume"
                  />
                </b>
              </TableCell>
              {isValidForPlannedFph(hasPlan, pvaData.pvaTableData.granularity)
                ? getPlannedInductTotalDatapoints(
                    shiftPlans,
                    plannedProcessPathMap,
                    lengthOfDataPointList,
                  ).map((volume, index) => (
                    <TableCell key={index}>{volume}</TableCell>
                  ))
                : pvaData.overviewData.flowPVAData[
                    pvaData.pvaTableData.granularity
                  ][INDUCT_PLANNED].dataPointList.map((datapoint, index) => (
                    <TableCell key={index}>
                      {datapoint.metricValue < 0
                        ? datapoint.metricValue === -1
                          ? "-"
                          : "0"
                        : datapoint.metricValue}
                    </TableCell>
                  ))}
            </TableRow>
          )}
          {inductActual ? (
            [
              <TableRow open={inductActual} onClick={onClickInductActual}>
                <TableCell width={headerWidth}>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.actual_volume"
                      defaultMessage="Actual Volume"
                    />
                  </b>
                </TableCell>
                {pvaData.overviewData.flowPVAData[
                  pvaData.pvaTableData.granularity
                ][INDUCT_ACTUALS].dataPointList.map((datapoint, index) => (
                  <TableCell key={index}>{datapoint.metricValue}</TableCell>
                ))}
              </TableRow>,
              <TableRow highlight={inductActual}>
                <TableCell>
                  &nbsp;&nbsp;
                  <FormattedMessage
                    id="package_overview.automation_volume"
                    defaultMessage="Automation Volume"
                  />
                </TableCell>
                {getActualInductMHEDatapoints(
                  pvaData.overviewData.flowPVAData[
                    pvaData.pvaTableData.granularity
                  ],
                ).map((metricValue, index) => (
                  <TableCell key={index}>{metricValue}</TableCell>
                ))}
              </TableRow>,

              <TableRow highlight={inductActual}>
                <TableCell>
                  &nbsp;&nbsp;
                  <FormattedMessage
                    id="package_overview.manual_volume"
                    defaultMessage="Manual Volume"
                  />
                </TableCell>
                {getActualInductManualDatapoints(
                  pvaData.overviewData.flowPVAData[
                    pvaData.pvaTableData.granularity
                  ],
                ).map((metricValue, index) => (
                  <TableCell key={index}>{metricValue}</TableCell>
                ))}
              </TableRow>,
            ]
          ) : (
            <TableRow open={inductActual} onClick={onClickInductActual}>
              <TableCell width={headerWidth}>
                <b>
                  <Icon
                    tokens={chevronRightSmallTokens}
                    className="chevron-margin"
                  />
                  <FormattedMessage
                    id="package_overview.actual_volume"
                    defaultMessage="Actual Volume"
                  />
                </b>
              </TableCell>
              {pvaData.overviewData.flowPVAData[
                pvaData.pvaTableData.granularity
              ][INDUCT_ACTUALS].dataPointList.map((datapoint, index) => (
                <TableCell key={index}>{datapoint.metricValue}</TableCell>
              ))}
            </TableRow>
          )}
          <TableRow>
            <TableCell>
              &nbsp;&nbsp;
              <FormattedMessage
                id="package_overview.target_pph"
                defaultMessage="Target PPH"
              />
            </TableCell>
            {pvaData.overviewData.flowPVAData[pvaData.pvaTableData.granularity][
              INDUCT_ACTUALS
            ].dataPointList.map((datapoint, index) => (
              <TableCell key={index}>
                {pvaData.pvaTableData.granularity !== "cumulative"
                  ? index %
                      (60 / parseInt(pvaData.pvaTableData.granularity, 10)) ===
                    0
                    ? hasPlan
                      ? targetPphInduct
                      : pvaData.targetData.INDUCT_PPH
                    : ""
                  : hasPlan
                    ? targetPphInduct
                    : pvaData.targetData.INDUCT_PPH}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell>
              &nbsp;&nbsp;
              <FormattedMessage
                id="package_overview.actual_pph"
                defaultMessage="Actual PPH"
              />
            </TableCell>
            {pvaData.performance[pvaData.pvaTableData.granularity]
              ? pvaData.performance[pvaData.pvaTableData.granularity].map(
                  (data) =>
                    data.inductPPH === -1 ? (
                      <TableCell key={data.time} />
                    ) : (
                      <TableCell key={data.time}>
                        {data.inductPPH.toFixed(0)}
                      </TableCell>
                    ),
                )
              : null}
          </TableRow>
        </TableRow>
      ) : (
        <TableRow open={induct} onClick={onClickInduct}>
          {/* Induct  Actuals*/}
          <TableCell width={headerWidth}>
            <b>
              <Icon
                tokens={chevronRightSmallTokens}
                className="chevron-margin"
              />
              <FormattedMessage
                id="package_overview.induct_actuals"
                defaultMessage="Induct Actuals"
              />
            </b>
          </TableCell>
          {pvaData.overviewData.flowPVAData[pvaData.pvaTableData.granularity][
            INDUCT_ACTUALS
          ].dataPointList.map((datapoint, index) => (
            <TableCell key={index}>{datapoint.metricValue}</TableCell>
          ))}
        </TableRow>
      )}
      <TableRow></TableRow>
      {/* Stow */}
      {stow ? (
        <TableRow open={stow} onClick={onClickStow}>
          <TableCell>
            <b>
              <FormattedMessage
                id="package_overview.stow"
                defaultMessage="Stow"
              />
            </b>
            <Tooltip
              position="right"
              title={intl.formatMessage({
                id: "tooltip.package_overview.stow",
              })}
            >
              <Icon className={"custom-tool-tip"} tokens={infoKnockoutTokens} />
            </Tooltip>
          </TableCell>
          <TableRow>
            <TableCell>
              &nbsp;&nbsp;
              <FormattedMessage
                id="package_overview.planned_volume"
                defaultMessage="Planned Volume"
              />
            </TableCell>
            {isValidForPlannedFph(hasPlan, pvaData.pvaTableData.granularity)
              ? shiftPlans
                  .filter((shiftPlan, index) => {
                    return (
                      shiftPlan.requiredHeadcounts.Stow &&
                      index < lengthOfDataPointList
                    );
                  })
                  .map((shiftPlan, index) => (
                    <TableCell key={index}>
                      {getPlannedFPHOfProcess(
                        plannedProcessPathMap,
                        shiftPlan.timeWindow.start,
                        STOW,
                      )}
                    </TableCell>
                  ))
              : pvaData.overviewData.flowPVAData[
                  pvaData.pvaTableData.granularity
                ][STOW_PLANNED].dataPointList.map((datapoint, index) => (
                  <TableCell key={index}>
                    {datapoint.metricValue < 0
                      ? datapoint.metricValue === -1
                        ? "-"
                        : "0"
                      : datapoint.metricValue}
                  </TableCell>
                ))}
          </TableRow>
          <TableRow>
            <TableCell>
              &nbsp;&nbsp;
              <FormattedMessage
                id="package_overview.actual_volume"
                defaultMessage="Actual Volume"
              />
            </TableCell>
            {pvaData.overviewData.flowPVAData[pvaData.pvaTableData.granularity][
              STOW_ACTUALS
            ].dataPointList.map((datapoint, index) => (
              <TableCell key={index}>{datapoint.metricValue}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell>
              &nbsp;&nbsp;
              <FormattedMessage
                id="package_overview.target_pph"
                defaultMessage="Target PPH"
              />
            </TableCell>
            {pvaData.overviewData.flowPVAData[
              pvaData.pvaTableData.granularity
            ][0].dataPointList.map((datapoint, index) => (
              <TableCell key={index}>
                {pvaData.pvaTableData.granularity !== "cumulative"
                  ? index %
                      (60 / parseInt(pvaData.pvaTableData.granularity, 10)) ===
                    0
                    ? hasPlan
                      ? targetPphStow
                      : pvaData.targetData.STOW_PPH
                    : ""
                  : hasPlan
                    ? targetPphStow
                    : pvaData.targetData.STOW_PPH}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell>
              &nbsp;&nbsp;
              <FormattedMessage
                id="package_overview.actual_PPH"
                defaultMessage="Actual PPH"
              />
            </TableCell>
            {pvaData.performance[pvaData.pvaTableData.granularity]
              ? pvaData.performance[pvaData.pvaTableData.granularity].map(
                  (data) =>
                    data.stowPPH === -1 ? (
                      <TableCell key={data.time} />
                    ) : (
                      <TableCell key={data.time}>
                        {data.stowPPH.toFixed(0)}
                      </TableCell>
                    ),
                )
              : null}
          </TableRow>
        </TableRow>
      ) : (
        <TableRow open={stow} onClick={onClickStow}>
          {/* Stow Actuals */}
          <TableCell>
            <b>
              <Icon
                tokens={chevronRightSmallTokens}
                className="chevron-margin"
              />
              <FormattedMessage
                id="package_overview.stow_actuals"
                defaultMessage="Stow Actuals"
              />
            </b>
          </TableCell>
          {pvaData.overviewData.flowPVAData[pvaData.pvaTableData.granularity][
            STOW_ACTUALS
          ].dataPointList.map((datapoint, index) => (
            <TableCell key={index}>{datapoint.metricValue}</TableCell>
          ))}
        </TableRow>
      )}
      {displayStation() ? (
        <TableSectionHeader>
          <b>
            <FormattedMessage
              id="package_overview.station"
              defaultMessage="Station"
            />
          </b>
        </TableSectionHeader>
      ) : null}
      {displayStation() ? (
        tph ? (
          <TableRow open={tph} onClick={onClickTph}>
            <TableCell>
              <b>
                <FormattedMessage
                  id="package_overview.tph"
                  defaultMessage="TPH"
                />
              </b>
              <Tooltip
                position="right"
                title={intl.formatMessage({
                  id: "tooltip.package_overview.tph",
                })}
              >
                <Icon
                  className={"custom-tool-tip"}
                  tokens={infoKnockoutTokens}
                />
              </Tooltip>
            </TableCell>
            <TableRow>
              <TableCell>
                &nbsp;&nbsp;
                <FormattedMessage
                  id="package_overview.actual_tph"
                  defaultMessage="Actual TPH"
                />
              </TableCell>
              {filteredPPAData.map((data) => {
                return !data.inboundThroughPut ||
                  data.inboundThroughPut.tph === -1 ? (
                  <TableCell key={data.time} />
                ) : (
                  <TableCell key={data.time}>
                    {data.inboundThroughPut.tph.toFixed(0)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableRow>
        ) : (
          <TableRow open={tph} onClick={onClickTph}>
            {/* TPH Actuals */}
            <TableCell>
              <b>
                <Icon
                  tokens={chevronRightSmallTokens}
                  className="chevron-margin"
                />
                <FormattedMessage
                  id="package_overview.tph_actuals"
                  defaultMessage="TPH Actuals"
                />
              </b>
            </TableCell>
            {filteredPPAData.map((data) => {
              return !data.inboundThroughPut ||
                data.inboundThroughPut.tph === -1 ? (
                <TableCell key={data.time} />
              ) : (
                <TableCell key={data.time}>
                  {data.inboundThroughPut.tph.toFixed(0)}
                </TableCell>
              );
            })}
          </TableRow>
        )
      ) : null}
      <TableRow></TableRow>
      {/* Labour */}
      {displayStation() ? (
        labour ? (
          <TableRow open={labour} onClick={onClickLabour}>
            <TableCell>
              <b>
                <FormattedMessage
                  id="package_overview.labour"
                  defaultMessage="Labour"
                />
              </b>
              <Tooltip
                position="right"
                title={intl.formatMessage({
                  id: "tooltip.package_overview.labour",
                })}
              >
                <Icon
                  className={"custom-tool-tip"}
                  tokens={infoKnockoutTokens}
                />
              </Tooltip>
            </TableCell>
            <TableRow>
              <TableCell>
                &nbsp;&nbsp;
                <FormattedMessage
                  id="package_overview.actual_hours"
                  defaultMessage="Actual Hours"
                />
              </TableCell>
              {
                // Actual Hours logic
                filteredPPAData.map((data) => {
                  return data.headCountDataMap["Actual Hours"] ? (
                    <TableCell key={data.time}>
                      {data.headCountDataMap["Actual Hours"]}{" "}
                    </TableCell>
                  ) : (
                    <TableCell key={data.time} />
                  );
                })
              }
            </TableRow>
            {hasPlan ? (
              <TableRow>
                <TableCell>
                  &nbsp;&nbsp;
                  <FormattedMessage
                    id="package_overview.planned_hours"
                    defaultMessage="Planned Hours"
                  />
                </TableCell>
                {
                  // Planned Hours logic
                  filteredPPAData.map((data) => {
                    return (
                      <TableCell key={data.time}>
                        {getPlannedHeadCount(data.time, "Planned")}{" "}
                      </TableCell>
                    );
                  })
                }
              </TableRow>
            ) : null}
            {sortPA ? (
              <TableRow open={sortPA} onClick={onClickSortPA}>
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    {hasPlan ? (
                      <FormattedMessage
                        id="package_overview.sort_pa"
                        defaultMessage="Sort(P/A)"
                      />
                    ) : (
                      <FormattedMessage
                        id="package_overview.sort_a"
                        defaultMessage="Sort(A)"
                      />
                    )}
                  </b>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow open={sortPA} onClick={onClickSortPA}>
                {/* Sort PA Actuals */}
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronRightSmallTokens}
                      className="chevron-margin"
                    />
                    {hasPlan ? (
                      <FormattedMessage
                        id="package_overview.sort_pa"
                        defaultMessage="Sort(P/A)"
                      />
                    ) : (
                      <FormattedMessage
                        id="package_overview.sort_a"
                        defaultMessage="Sort(A)"
                      />
                    )}
                  </b>
                </TableCell>
                {
                  // Sort logic
                  generateMainProcessTotals(filteredPPAData, "Sort")
                }
              </TableRow>
            )}
            {sortPA
              ? // Sort sub processes logic
                generateSubProcessTotals(filteredPPAData, SORT, "Sort")
              : null}
            {stagePA ? (
              <TableRow open={stagePA} onClick={onClickStagePA}>
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    {hasPlan ? (
                      <FormattedMessage
                        id="package_overview.stage_pa"
                        defaultMessage="Pick Stage(P/A)"
                      />
                    ) : (
                      <FormattedMessage
                        id="package_overview.stage_a"
                        defaultMessage="Pick Stage(A)"
                      />
                    )}
                  </b>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow open={stagePA} onClick={onClickStagePA}>
                {/* Pick Stage PA Actuals */}
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronRightSmallTokens}
                      className="chevron-margin"
                    />
                    {hasPlan ? (
                      <FormattedMessage
                        id="package_overview.stage_pa"
                        defaultMessage="Pick Stage(P/A)"
                      />
                    ) : (
                      <FormattedMessage
                        id="package_overview.stage_a"
                        defaultMessage="Pick Stage(A)"
                      />
                    )}
                  </b>
                </TableCell>
                {
                  // Pick Stage Logic
                  generateMainProcessTotals(filteredPPAData, "Pick Stage")
                }
              </TableRow>
            )}
            {stagePA
              ? // Sort sub processes logic
                generateSubProcessTotals(
                  filteredPPAData,
                  PICK_STAGE,
                  "Pick Stage",
                )
              : null}
            {utrPA ? (
              <TableRow open={utrPA} onClick={onClickUtrPA}>
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    {hasPlan ? (
                      <FormattedMessage
                        id="package_overview.utr_pa"
                        defaultMessage="UTR(P/A)"
                      />
                    ) : (
                      <FormattedMessage
                        id="package_overview.utr_a"
                        defaultMessage="UTR(A)"
                      />
                    )}
                  </b>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow open={utrPA} onClick={onClickUtrPA}>
                {/* UTR PA Actuals */}
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronRightSmallTokens}
                      className="chevron-margin"
                    />
                    {hasPlan ? (
                      <FormattedMessage
                        id="package_overview.utr_pa"
                        defaultMessage="UTR(P/A)"
                      />
                    ) : (
                      <FormattedMessage
                        id="package_overview.utr_a"
                        defaultMessage="UTR(A)"
                      />
                    )}
                  </b>
                </TableCell>
                {
                  // UTR logic
                  generateMainProcessTotals(filteredPPAData, "UTR")
                }
              </TableRow>
            )}
            {utrPA
              ? generateSubProcessTotals(filteredPPAData, UTR, "UTR")
              : null}
            {siteSupportPA ? (
              <TableRow open={siteSupportPA} onClick={onClickSiteSupportPA}>
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    {hasPlan ? (
                      <FormattedMessage
                        id="package_overview.ss_pa"
                        defaultMessage="Site Support(P/A)"
                      />
                    ) : (
                      <FormattedMessage
                        id="package_overview.ss_a"
                        defaultMessage="Site Support(A)"
                      />
                    )}
                  </b>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow open={siteSupportPA} onClick={onClickSiteSupportPA}>
                {/* Site Support(P/A) Actuals */}
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronRightSmallTokens}
                      className="chevron-margin"
                    />
                    {hasPlan ? (
                      <FormattedMessage
                        id="package_overview.ss_pa"
                        defaultMessage="Site Support(P/A)"
                      />
                    ) : (
                      <FormattedMessage
                        id="package_overview.ss_a"
                        defaultMessage="Site Support(A)"
                      />
                    )}
                  </b>
                </TableCell>
                {
                  // Site Support logic
                  generateMainProcessTotals(filteredPPAData, "Site Support")
                }
              </TableRow>
            )}
            {siteSupportPA
              ? generateSubProcessTotals(
                  filteredPPAData,
                  SITE_SUPPORT,
                  "Site Support",
                )
              : null}
            {otrPA ? (
              <TableRow open={otrPA} onClick={onClickOtrPA}>
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.otr_pa"
                      defaultMessage="OTR(A)"
                    />
                  </b>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow open={otrPA} onClick={onClickOtrPA}>
                {/* OTR PA Actuals */}
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronRightSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.otr_pa"
                      defaultMessage="OTR(A)"
                    />
                  </b>
                </TableCell>
                {
                  // OTR(A) actuals logic
                  filteredPPAData.map((data) => {
                    return data.headCountDataMap["OTR"] &&
                      data.headCountDataMap["OTR"]["Actuals"] ? (
                      <TableCell key={data.time}>
                        {" "}
                        {data.headCountDataMap["OTR"]["Actuals"]}{" "}
                      </TableCell>
                    ) : (
                      <TableCell key={data.time}>
                        {" "}
                        {DEFAULT_HEAD_COUNT}{" "}
                      </TableCell>
                    );
                  })
                }
              </TableRow>
            )}
            {otrPA
              ? Object.keys(OTR).map((key) => (
                  <TableRow>
                    <TableCell width={headerWidth}>
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      <FormattedMessage
                        id={OTR[key].id}
                        defaultMessage={OTR[key].name}
                      ></FormattedMessage>
                    </TableCell>
                    {
                      // Stow logic
                      filteredPPAData.map((data) => {
                        return Object.keys(data.headCountDataMap).length > 1 &&
                          data.headCountDataMap["OTR"] &&
                          data.headCountDataMap["OTR"][OTR[key].alias] ? (
                          <TableCell key={data.time}>
                            {capDecimal(
                              data.headCountDataMap["OTR"][OTR[key].alias][0]
                                .headCount,
                            )}{" "}
                          </TableCell>
                        ) : (
                          <TableCell key={data.time}>
                            {" "}
                            {DEFAULT_HEAD_COUNT}{" "}
                          </TableCell>
                        );
                      })
                    }
                  </TableRow>
                ))
              : null}
            {swaPA ? (
              <TableRow open={swaPA} onClick={onClickSwaPA}>
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.swa_pa"
                      defaultMessage="SWA(A)"
                    />
                  </b>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow open={swaPA} onClick={onClickSwaPA}>
                {/* SWA PA Actuals */}
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronRightSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.swa_pa"
                      defaultMessage="SWA(A)"
                    />
                  </b>
                </TableCell>
                {
                  // SWA(A) actuals logic
                  filteredPPAData.map((data) => {
                    return data.headCountDataMap["SWA"] &&
                      data.headCountDataMap["SWA"]["Actuals"] ? (
                      <TableCell key={data.time}>
                        {" "}
                        {data.headCountDataMap["SWA"]["Actuals"]}{" "}
                      </TableCell>
                    ) : (
                      <TableCell key={data.time}>
                        {" "}
                        {DEFAULT_HEAD_COUNT}{" "}
                      </TableCell>
                    );
                  })
                }
              </TableRow>
            )}
            {swaPA
              ? Object.keys(SWA).map((key) => (
                  <TableRow>
                    <TableCell width={headerWidth}>
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      <FormattedMessage
                        id={SWA[key].id}
                        defaultMessage={SWA[key].name}
                      ></FormattedMessage>
                    </TableCell>
                    {
                      // Stow logic
                      filteredPPAData.map((data) => {
                        return Object.keys(data.headCountDataMap).length > 1 &&
                          data.headCountDataMap["SWA"] &&
                          data.headCountDataMap["SWA"][SWA[key].alias] ? (
                          <TableCell key={data.time}>
                            {capDecimal(
                              data.headCountDataMap["SWA"][SWA[key].alias][0]
                                .headCount,
                            )}{" "}
                          </TableCell>
                        ) : (
                          <TableCell key={data.time}>
                            {" "}
                            {DEFAULT_HEAD_COUNT}{" "}
                          </TableCell>
                        );
                      })
                    }
                  </TableRow>
                ))
              : null}
            {rtsPA ? (
              <TableRow open={rtsPA} onClick={onClickRtsPA}>
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.rts_pa"
                      defaultMessage="RTS(A)"
                    />
                  </b>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow open={rtsPA} onClick={onClickRtsPA}>
                {/* RTS PA Actuals */}
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronRightSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.rts_pa"
                      defaultMessage="RTS(A)"
                    />
                  </b>
                </TableCell>
                {
                  // RTS(A) actuals logic
                  filteredPPAData.map((data) => {
                    return data.headCountDataMap["RTS"] &&
                      data.headCountDataMap["RTS"]["Actuals"] ? (
                      <TableCell key={data.time}>
                        {" "}
                        {data.headCountDataMap["RTS"]["Actuals"]}{" "}
                      </TableCell>
                    ) : (
                      <TableCell key={data.time}>
                        {" "}
                        {DEFAULT_HEAD_COUNT}{" "}
                      </TableCell>
                    );
                  })
                }
              </TableRow>
            )}
            {rtsPA
              ? Object.keys(RTS).map((key) => (
                  <TableRow>
                    <TableCell width={headerWidth}>
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      <FormattedMessage
                        id={RTS[key].id}
                        defaultMessage={RTS[key].name}
                      ></FormattedMessage>
                    </TableCell>
                    {
                      // Stow logic
                      filteredPPAData.map((data) => {
                        return Object.keys(data.headCountDataMap).length > 1 &&
                          data.headCountDataMap["RTS"] &&
                          data.headCountDataMap["RTS"][RTS[key].alias] ? (
                          <TableCell key={data.time}>
                            {capDecimal(
                              data.headCountDataMap["RTS"][RTS[key].alias][0]
                                .headCount,
                            )}{" "}
                          </TableCell>
                        ) : (
                          <TableCell key={data.time}>
                            {" "}
                            {DEFAULT_HEAD_COUNT}{" "}
                          </TableCell>
                        );
                      })
                    }
                  </TableRow>
                ))
              : null}
            {nonCoreSupportPA ? (
              <TableRow
                open={nonCoreSupportPA}
                onClick={onClickNonCoreSupportPA}
              >
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronDownSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.ncs_pa"
                      defaultMessage="Non-Core Support(A)"
                    />
                  </b>
                </TableCell>
              </TableRow>
            ) : (
              <TableRow
                open={nonCoreSupportPA}
                onClick={onClickNonCoreSupportPA}
              >
                {/* NonCoreSupport PA Actuals */}
                <TableCell>
                  <b>
                    <Icon
                      tokens={chevronRightSmallTokens}
                      className="chevron-margin"
                    />
                    <FormattedMessage
                      id="package_overview.ncs_pa"
                      defaultMessage="Non-Core Support(A)"
                    />
                  </b>
                </TableCell>
                {
                  // NonCoreSupport(A) actuals logic
                  filteredPPAData.map((data) => {
                    return data.headCountDataMap["Non-Core Support"] &&
                      data.headCountDataMap["Non-Core Support"]["Actuals"] ? (
                      <TableCell key={data.time}>
                        {" "}
                        {
                          data.headCountDataMap["Non-Core Support"]["Actuals"]
                        }{" "}
                      </TableCell>
                    ) : (
                      <TableCell key={data.time}>
                        {" "}
                        {DEFAULT_HEAD_COUNT}{" "}
                      </TableCell>
                    );
                  })
                }
              </TableRow>
            )}
            {nonCoreSupportPA
              ? Object.keys(NonCoreSupport).map((key) => (
                  <TableRow>
                    <TableCell width={headerWidth}>
                      &nbsp;&nbsp;&nbsp;&nbsp;
                      <FormattedMessage
                        id={NonCoreSupport[key].id}
                        defaultMessage={NonCoreSupport[key].name}
                      ></FormattedMessage>
                    </TableCell>
                    {
                      // Stow logic
                      filteredPPAData.map((data) => {
                        return Object.keys(data.headCountDataMap).length > 1 &&
                          data.headCountDataMap["Non-Core Support"] &&
                          data.headCountDataMap["Non-Core Support"][
                            NonCoreSupport[key].alias
                          ] ? (
                          <TableCell key={data.time}>
                            {capDecimal(
                              data.headCountDataMap["Non-Core Support"][
                                NonCoreSupport[key].alias
                              ][0].headCount,
                            )}{" "}
                          </TableCell>
                        ) : (
                          <TableCell key={data.time}>
                            {" "}
                            {DEFAULT_HEAD_COUNT}{" "}
                          </TableCell>
                        );
                      })
                    }
                  </TableRow>
                ))
              : null}

            <TableRow>
              {/* Off Task(A) Actuals */}
              <TableCell>
                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <b>
                  <FormattedMessage
                    id="package_overview.off_task"
                    defaultMessage="Off Task(A)"
                  />
                </b>
              </TableCell>
              {
                // Off Task(A) Actuals logic
                // Since Off Task will have only one Labor process i.e. Off Task, Actuals will be same as headCount
                filteredPPAData.map((data) => {
                  return Object.keys(data.headCountDataMap).length > 1 &&
                    data.headCountDataMap["Off Task"] &&
                    data.headCountDataMap["Off Task"]["Actuals"] ? (
                    <TableCell key={data.time}>
                      {" "}
                      {data.headCountDataMap["Off Task"]["Actuals"]}{" "}
                    </TableCell>
                  ) : (
                    <TableCell key={data.time}>
                      {" "}
                      {DEFAULT_HEAD_COUNT}{" "}
                    </TableCell>
                  );
                })
              }
            </TableRow>
          </TableRow>
        ) : (
          <TableRow open={labour} onClick={onClickLabour}>
            {/* Labour Actuals */}
            <TableCell>
              <b>
                <Icon
                  tokens={chevronRightSmallTokens}
                  className="chevron-margin"
                />
                <FormattedMessage
                  id="package_overview.labour_actuals"
                  defaultMessage="Labour Actuals"
                />
              </b>
            </TableCell>
            {
              // Labour Actuals logic
              filteredPPAData.map((data) => {
                return data.headCountDataMap["Actual Hours"] ? (
                  <TableCell key={data.time}>
                    {" "}
                    {data.headCountDataMap["Actual Hours"]}{" "}
                  </TableCell>
                ) : (
                  <TableCell key={data.time} />
                );
              })
            }
          </TableRow>
        )
      ) : null}
    </Table>
  );
};

export default injectIntl(PackageOverviewInbound);



// WEBPACK FOOTER //
// ./src/components/packageOverview/PackageOverviewInboundv2.js